import React from "react"
import { useState, useEffect, useRef } from "react"
import ReactModal from "react-modal"
import PestaniaProyecto from "./PestaniaProyecto"
import PestaniaProyectoSperant from "./PestaniaProyectoSperant"

import "slick-carousel/slick/slick.css"
import "slick-carousel/slick/slick-theme.css"
import "./CotizaProyecto.scss"
import { drupalLogout } from "./ApiCotizar"
import FormGracias from "../Formularios/FormGracias"

import SperantProjectsList from "../../components/Sperant/SperantProjectsList"

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    borderRadius: "none",
    overflow: "initial",
  },
}

if (typeof window !== "undefined") {
  ReactModal.setAppElement("body")
}

// Normaliza el array plano de tipologías WP (desde pageContext) al formato
// que ya consumen PestaniaProyectoSperant y DormitorioProyecto.
// Retorna { nroDorm, starsCount, bedroomLabels } donde:
//   nroDorm      — array de números únicos de dormitorios, ordenados ASC
//   starsCount   — array de arrays de tipologías agrupadas por dormitorio
//   bedroomLabels — array de etiquetas visibles para cada pestaña
const normalizarTipologiasWP = (tipologias) => {
  if (!tipologias || tipologias.length === 0) {
    return { nroDorm: [], starsCount: [], bedroomLabels: [] }
  }

  const gruposMap = new Map()

  tipologias.forEach(t => {
    const key = t.bedrooms
    if (!gruposMap.has(key)) {
      const label = t.bedroomLabel
        || `${t.bedrooms} Dormitorio${t.bedrooms !== 1 ? 's' : ''}`
      gruposMap.set(key, { label, items: [] })
    }
    gruposMap.get(key).items.push({
      modelo: t.typologyName,
      nroDormitorio: t.bedrooms,
      nroBanios: t.bathrooms,
      areaConstruida: parseFloat(t.area) || 0,
      areaVenta: parseFloat(t.area) || 0,
      urlImagen: t.image || '',
      title: t.typologyName,
      codInmueble: t.typologyCode,
      precio: t.priceFrom || 0,
      unidadesDisponibles: 0,
      wpTypologyId: t.id,
      crmTypologyId: t.crmTypologyId || null,
    })
  })

  const nroDorm = [...gruposMap.keys()]
  const starsCount = nroDorm.map(n => gruposMap.get(n).items)
  const bedroomLabels = nroDorm.map(n => gruposMap.get(n).label)

  return { nroDorm, starsCount, bedroomLabels }
}

const CotizarProyecto = ({
  titulo,
  textoBoton,
  idProyecto,
  nomProyecto,
  isGracias,
  urlGracias,
  tituloGracias,
  parrafoGracias,
  correoCotiza,
  urlWsp,
  idPage,
  projectSlug,
  tipologiasWP,
  distritoProyecto,
}) => {
  const configuracion = {
    dots: false,
    arrows: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplaySpeed: 4000,
    pauseOnHover: false,
    pauseOnFocus: false,
    lazyLoad: true,
  }

  const [starsCount, setStarsCount] = useState([])
  const [nroDorm, setNroDorm] = useState([])

  // Estados para datos de Sperant (fallback cuando no hay tipologías WP)
  const [sperantStarsCount, setSperantStarsCount] = useState([])
  const [sperantNroDorm, setSperantNroDorm] = useState([])
  const [sperantData, setSperantData] = useState(null)

  const [isModalOpen, setIsModalOpen] = useState(isGracias)
  const mountedRef = useRef(true)

  const handleModalClose = event => {
    setIsModalOpen(false)
  }

  const objectComparisonCallback = (arrayItemA, arrayItemB) => {
    var modelo_a = arrayItemA.modelo.slice(16)
    if (modelo_a < arrayItemB.modelo) {
      return -1
    }

    if (arrayItemA.modelo > arrayItemB.modelo) {
      return 1
    }

    return 0
  }

  // Función para transformar datos de Sperant al formato local
  const transformSperantData = (sperantTypes) => {
    if (!sperantTypes || !Array.isArray(sperantTypes)) {
      return { nroDorm: [], starsCount: [] };
    }

    // Filtrar solo tipologías con units_available > 0
    // Para pruebas, puedes comentar esta línea y usar la línea alternativa
    const availableTypes = sperantTypes.filter(type =>
      type.attributes && type.attributes.units_available > 0
    );

    // Línea alternativa para pruebas (incluye todas las tipologías):
    // const availableTypes = sperantTypes.filter(type => type.attributes);


    // Transformar cada tipología al formato local
    const transformedData = availableTypes.map(type => {
      const attr = type.attributes;
      return {
        modelo: attr.name || `Tipo ${attr.id}`,
        tipoInmueble: "Departamento", // Por defecto
        nroDormitorio: attr.num_bedroom || 0,
        nroBanios: attr.num_bathroom || 0,
        areaJardin: 0,
        areaTerraza: 0,
        areaLibre: 0,
        areaConstruida: attr.area || 0,
        areatechada: attr.area || 0,
        areaVenta: attr.area || 0,
        urlImagen: attr.image || "",
        codInmueble: attr.id,
        precio: attr.price_min || 0,
        unidadesDisponibles: attr.units_available || 0,
        // Datos adicionales de Sperant
        sperantId: attr.id,
        totalUnidades: attr.total_units || 0,
        descripcion: attr.description || ""
      };
    });

    // Obtener dormitorios únicos y ordenarlos
    const dormitorios = [...new Set(transformedData.map(item => item.nroDormitorio))].sort();

    // Agrupar por número de dormitorios
    const groupedData = dormitorios.map(numDorm =>
      transformedData.filter(item => item.nroDormitorio === numDorm)
    );

    return {
      nroDorm: dormitorios,
      starsCount: groupedData
    };
  }

  useEffect(() => {
    const fetchData = async () => {
      if (idProyecto) {
        const resultados = await drupalLogout(idProyecto)
        if (resultados) {
          let datos = Object.values(resultados),
            dorm = [],
            newDatos = []
          datos.map(dato => (dorm = [...dorm, dato?.nroDormitorio]))
          const nroDorm = dorm
            .filter((valor, indice) => dorm.indexOf(valor) === indice)
            .sort()
          nroDorm.map(
            nro =>
            (newDatos = [
              ...newDatos,
              datos.filter(dato => dato?.nroDormitorio === nro),
            ])
          )

          newDatos.forEach(element => {
            element.sort(objectComparisonCallback)
          })

          setNroDorm(nroDorm)
          setStarsCount(newDatos)
        }
      }
    }

    fetchData()

    return () => {
      mountedRef.current = false
      setNroDorm("")
      setStarsCount([])
      setIsModalOpen(false)
    }
  }, [idProyecto])


  // Crear la variable sperantId
  let sperantId = null;
  if (idPage == 5395) {
    sperantId = 528;
  }

  // Datos normalizados desde WordPress (build-time, sin fetch)
  const wpNormalized = normalizarTipologiasWP(tipologiasWP || [])
  const useWpData = wpNormalized.nroDorm.length > 0

  // Fuente activa: WP si ya tiene tipologías migradas, Sperant como fallback
  const activeNroDorm = useWpData ? wpNormalized.nroDorm : sperantNroDorm
  const activeStarsCount = useWpData ? wpNormalized.starsCount : sperantStarsCount
  const activeBedroomLabels = useWpData ? wpNormalized.bedroomLabels : null

  return (
    <section className="cotizar-seccion visible" id="cotizar">

      {/* Sperant se carga solo si no hay tipologías en WP todavía */}
      {!useWpData && (
        <SperantProjectsList
          sperantId={idProyecto}
          onDataReceived={(data) => {
            setSperantData(data);
            const transformed = transformSperantData(data);
            setSperantNroDorm(transformed.nroDorm);
            setSperantStarsCount(transformed.starsCount);
          }}
        />
      )}

      <div className="cotizar-contenido">
        {titulo && (
          <h3 className="ubicacion-titulo size-45 font-roboto weight-300 text-center">
            {titulo}
          </h3>
        )}

        <PestaniaProyectoSperant
          nroDorm={activeNroDorm}
          starsCount={activeStarsCount}
          bedroomLabels={activeBedroomLabels}
          configuracion={configuracion}
          textoBoton={textoBoton}
          nomProyecto={nomProyecto}
          urlGracias={urlGracias}
          correoCotiza={correoCotiza}
          idProyecto={idProyecto}
          urlWsp={urlWsp}
          idPage={idPage}
          projectSlug={projectSlug}
          distritoProyecto={distritoProyecto}
        />


        <ReactModal
          isOpen={isModalOpen}
          onRequestClose={handleModalClose}
          contentLabel="Modal Cotizar"
          style={customStyles}
        >
          <button onClick={handleModalClose} className="form-cerrar">
            x
          </button>
          {isGracias && (
            <FormGracias titulo={tituloGracias} parrafo={parrafoGracias} />
          )}
        </ReactModal>
      </div>
    </section>
  )
}

export default CotizarProyecto
